'use client'
import { useState } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { Fragment } from 'react'
import toast from 'react-hot-toast'
import { X } from 'lucide-react'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'

interface ExportResultTrackingModalProps {
    isOpen: boolean
    onClose: () => void
    onExport: (data: {
        courseId: string
        creationDate: string
        completeDate: string
    }) => Promise<void>
    courseId: string
    isLoading?: boolean
}

export default function ExportResultTrackingModal({
    isOpen,
    onClose,
    onExport,
    courseId,
    isLoading = false
}: ExportResultTrackingModalProps) {
    const [creationDate, setCreationDate] = useState<Date | null>(null)
    const [completeDate, setCompleteDate] = useState<Date | null>(null)

    const handleSubmit = async () => {
        if (!creationDate || !completeDate) {
            toast.error("Vui lòng nhập đầy đủ thông tin")
            return
        }

        try {
            await onExport({
                courseId,
                creationDate: creationDate.toISOString(),
                completeDate: completeDate.toISOString()
            })
            onClose()
            // Reset form
            setCreationDate(null)
            setCompleteDate(null)
        } catch (error) {
            console.error('Export failed:', error)
        }
    }

    return (
        <Transition appear show={isOpen} as={Fragment}>
            <Dialog as="div" className="relative z-50" onClose={onClose}>
                <Transition.Child
                    as={Fragment}
                    enter="ease-out duration-300"
                    enterFrom="opacity-0"
                    enterTo="opacity-100"
                    leave="ease-in duration-200"
                    leaveFrom="opacity-100"
                    leaveTo="opacity-0"
                >
                    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm"/>
                </Transition.Child>

                <div className="fixed inset-0 overflow-y-auto">
                    <div className="flex min-h-full items-center justify-center p-4 text-center">
                        <Transition.Child
                            as={Fragment}
                            enter="ease-out duration-300"
                            enterFrom="opacity-0 scale-95"
                            enterTo="opacity-100 scale-100"
                            leave="ease-in duration-200"
                            leaveFrom="opacity-100 scale-100"
                            leaveTo="opacity-0 scale-95"
                        >
                            <Dialog.Panel
                                className="w-full max-w-md transform overflow-visible rounded-xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                                <div className="flex justify-between items-center mb-4">
                                    <Dialog.Title
                                        as="h3"
                                        className="text-lg font-semibold leading-6 text-gray-900"
                                    >
                                        Xuất Sổ theo dõi
                                    </Dialog.Title>
                                    <button
                                        onClick={onClose}
                                        className="text-gray-500 hover:text-gray-700 focus:outline-none"
                                    >
                                        <X/>
                                    </button>
                                </div>

                                <div className="space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Ngày lập <span className="text-red-500">*</span>
                                        </label>
                                        <DatePicker
                                            selected={creationDate}
                                            onChange={(date) => setCreationDate(date)}
                                            dateFormat="dd/MM/yyyy"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            wrapperClassName="w-full relative"
                                            calendarClassName="!border !border-gray-200 !rounded-lg !shadow-sm"
                                            placeholderText="Chọn ngày lập"
                                            calendarStartDay={1}
                                            showPopperArrow={false}
                                            popperClassName="!z-[9999]"
                                            popperPlacement="bottom-start"
                                            popperModifiers={[
                                                {
                                                    name: 'offset',
                                                    options: {
                                                        offset: [0, 8]
                                                    }
                                                },
                                                {
                                                    name: 'preventOverflow',
                                                    options: {
                                                        rootBoundary: 'viewport',
                                                        tether: false,
                                                        altAxis: true
                                                    }
                                                }
                                            ]}
                                            renderCustomHeader={({
                                                date,
                                                decreaseMonth,
                                                increaseMonth,
                                                prevMonthButtonDisabled,
                                                nextMonthButtonDisabled,
                                            }) => (
                                                <div className="flex items-center justify-between px-2 py-2">
                                                    <button
                                                        onClick={decreaseMonth}
                                                        disabled={prevMonthButtonDisabled}
                                                        type="button"
                                                        className={`p-1.5 rounded-lg hover:bg-gray-100 transition-colors ${
                                                            prevMonthButtonDisabled ? 'opacity-50 cursor-not-allowed' : ''
                                                        }`}
                                                        aria-label="Tháng trước"
                                                    >
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            className="h-5 w-5 text-gray-600"
                                                            viewBox="0 0 20 20"
                                                            fill="currentColor"
                                                        >
                                                            <path
                                                                fillRule="evenodd"
                                                                d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                                                                clipRule="evenodd"
                                                            />
                                                        </svg>
                                                    </button>

                                                    <span className="text-sm font-semibold text-gray-800">
                                                        {date.toLocaleDateString('vi-VN', { month: 'long', year: 'numeric' })}
                                                    </span>

                                                    <button
                                                        onClick={increaseMonth}
                                                        disabled={nextMonthButtonDisabled}
                                                        type="button"
                                                        className={`p-1.5 rounded-lg hover:bg-gray-100 transition-colors ${
                                                            nextMonthButtonDisabled ? 'opacity-50 cursor-not-allowed' : ''
                                                        }`}
                                                        aria-label="Tháng sau"
                                                    >
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            className="h-5 w-5 text-gray-600"
                                                            viewBox="0 0 20 20"
                                                            fill="currentColor"
                                                        >
                                                            <path
                                                                fillRule="evenodd"
                                                                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                                                clipRule="evenodd"
                                                            />
                                                        </svg>
                                                    </button>
                                                </div>
                                            )}
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            Ngày hoàn thành khóa học <span className="text-red-500">*</span>
                                        </label>
                                        <DatePicker
                                            selected={completeDate}
                                            onChange={(date) => setCompleteDate(date)}
                                            dateFormat="dd/MM/yyyy"
                                            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                            wrapperClassName="w-full relative"
                                            calendarClassName="!border !border-gray-200 !rounded-lg !shadow-sm"
                                            placeholderText="Chọn ngày hoàn thành"
                                            calendarStartDay={1}
                                            showPopperArrow={false}
                                            popperClassName="!z-[9999]"
                                            popperPlacement="bottom-start"
                                            popperModifiers={[
                                                {
                                                    name: 'offset',
                                                    options: {
                                                        offset: [0, 8]
                                                    }
                                                },
                                                {
                                                    name: 'preventOverflow',
                                                    options: {
                                                        rootBoundary: 'viewport',
                                                        tether: false,
                                                        altAxis: true
                                                    }
                                                }
                                            ]}
                                            renderCustomHeader={({
                                                date,
                                                decreaseMonth,
                                                increaseMonth,
                                                prevMonthButtonDisabled,
                                                nextMonthButtonDisabled,
                                            }) => (
                                                <div className="flex items-center justify-between px-2 py-2">
                                                    <button
                                                        onClick={decreaseMonth}
                                                        disabled={prevMonthButtonDisabled}
                                                        type="button"
                                                        className={`p-1.5 rounded-lg hover:bg-gray-100 transition-colors ${
                                                            prevMonthButtonDisabled ? 'opacity-50 cursor-not-allowed' : ''
                                                        }`}
                                                        aria-label="Tháng trước"
                                                    >
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            className="h-5 w-5 text-gray-600"
                                                            viewBox="0 0 20 20"
                                                            fill="currentColor"
                                                        >
                                                            <path
                                                                fillRule="evenodd"
                                                                d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                                                                clipRule="evenodd"
                                                            />
                                                        </svg>
                                                    </button>

                                                    <span className="text-sm font-semibold text-gray-800">
                                                        {date.toLocaleDateString('vi-VN', { month: 'long', year: 'numeric' })}
                                                    </span>

                                                    <button
                                                        onClick={increaseMonth}
                                                        disabled={nextMonthButtonDisabled}
                                                        type="button"
                                                        className={`p-1.5 rounded-lg hover:bg-gray-100 transition-colors ${
                                                            nextMonthButtonDisabled ? 'opacity-50 cursor-not-allowed' : ''
                                                        }`}
                                                        aria-label="Tháng sau"
                                                    >
                                                        <svg
                                                            xmlns="http://www.w3.org/2000/svg"
                                                            className="h-5 w-5 text-gray-600"
                                                            viewBox="0 0 20 20"
                                                            fill="currentColor"
                                                        >
                                                            <path
                                                                fillRule="evenodd"
                                                                d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                                                                clipRule="evenodd"
                                                            />
                                                        </svg>
                                                    </button>
                                                </div>
                                            )}
                                        />
                                    </div>
                                </div>

                                <div className="mt-6 flex justify-end space-x-3">
                                    <button
                                        type="button"
                                        onClick={onClose}
                                        disabled={isLoading}
                                        className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-lg hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50"
                                    >
                                        Hủy
                                    </button>
                                    <button
                                        type="button"
                                        onClick={handleSubmit}
                                        disabled={isLoading}
                                        className="px-4 py-2 text-sm font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50"
                                    >
                                        {isLoading ? 'Đang xuất...' : 'Xuất file'}
                                    </button>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </Dialog>
        </Transition>
    )
}
